// MediaLibraryView — the Media Library PAGE, pure & props-driven (WL-023).
// Renders deck states 01 (populated), 02 (bulk move/selection), 04 (empty),
// 05 (list view) + async loading/empty/error (WL-027 acceptance). No data
// fetching here — the container (pages/MediaLibrary) wires redux/api to it, so
// this layer is unit/visual-testable in isolation.
import React from "react";
import * as S from "./MediaLibrary.styled";
import { MediaGrid, MediaListTable } from "./MediaGrid";
import ErrorWheel from "../ErrorWheel";
import {
  IconAllTiles, IconImage, IconVideo, IconAudio, IconDocument,
  IconGridLarge, IconGridSmall, IconList, IconFolder, IconSearch,
  IconSort, IconChevronLeft, IconChevronRight, IconPlus, IconUpload, IconCapture,
  IconTrash,
} from "./icons";
import { TYPE_TABS, PER_PAGE_OPTIONS, pageCount } from "../../pages/MediaLibrary/helpers";

const TAB_ICON = {
  all: IconAllTiles, images: IconImage, videos: IconVideo, audio: IconAudio, documents: IconDocument,
  captures: IconCapture,
};

function StorageStrip({ usedLabel, totalLabel, percent, onUpgrade, exceeded }) {
  return (
    <S.StorageWrap aria-label={exceeded ? "Storage usage (over plan storage)" : "Storage usage"}>
      <S.StorageCaptionRow>
        <S.StorageLabel>
          <b>{usedLabel}</b> of {totalLabel} used
        </S.StorageLabel>
        <S.StorageUpgradeButton
          type="button"
          data-testid="storage-upgrade-cta"
          onClick={onUpgrade ? () => onUpgrade() : undefined}
        >
          Upgrade
        </S.StorageUpgradeButton>
      </S.StorageCaptionRow>
      <S.StorageTrack role="progressbar" aria-valuenow={Math.round(percent)} aria-valuemin={0} aria-valuemax={100}>
        <S.StorageFill width={percent} $over={exceeded} />
      </S.StorageTrack>
      {exceeded && (
        <S.StorageOverCaption data-testid="storage-over-caption">
          You're over your plan storage
        </S.StorageOverCaption>
      )}
    </S.StorageWrap>
  );
}

// EW-1282: New Folder / Move-to-Folder have no backend yet (no media-folder or
// media-move API — see index.jsx). Disabled + "coming soon" tooltip, matching
// the app's existing convention for not-yet-wired controls (WheelLocking
// Payment, CreatorAnalytics date-range, GalleryTheme upload-url) rather than a
// button that looks live but silently does nothing on click.
const FOLDERS_COMING_SOON = "Organizing media into folders is coming soon";

function FoldersRow({ folders, onOpenFolder }) {
  return (
    <>
      <S.SectionLabel>Folders <span>({folders.length})</span></S.SectionLabel>
      <S.FoldersRow>
        {folders.map((f) => (
          <S.FolderCard key={f.id} onClick={() => onOpenFolder && onOpenFolder(f)} aria-label={`Open folder ${f.name}, ${f.count} items`}>
            <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <span style={{ color: "#9B5AF8", display: "inline-flex" }}><IconFolder size={24} /></span>
              <S.FolderMeta>
                <span className="name" title={f.name}>{f.name}</span>
                <span className="count">{f.count} items</span>
              </S.FolderMeta>
            </span>
          </S.FolderCard>
        ))}
        <S.NewFolderTile type="button" disabled aria-disabled="true" title={FOLDERS_COMING_SOON}>
          <IconPlus size={20} />
          <span>New Folder <span style={{ fontWeight: 400 }}>(coming soon)</span></span>
        </S.NewFolderTile>
      </S.FoldersRow>
    </>
  );
}

function TypeTabs({ active, counts = {}, onChange }) {
  return (
    <S.Tabs role="tablist" aria-label="Media type">
      {TYPE_TABS.map((t) => {
        const Ico = TAB_ICON[t.key];
        return (
          <S.Tab
            key={t.key}
            role="tab"
            aria-selected={active === t.key}
            active={active === t.key ? 1 : 0}
            onClick={() => onChange && onChange(t.key)}
          >
            <Ico size={18} /> {t.label}
          </S.Tab>
        );
      })}
    </S.Tabs>
  );
}

function ViewToggle({ mode, onChange }) {
  const segs = [
    { key: "large", label: "Large thumbnails", Ico: IconGridLarge },
    { key: "small", label: "Small thumbnails", Ico: IconGridSmall },
    { key: "list", label: "List view", Ico: IconList },
  ];
  return (
    <S.ViewToggle role="group" aria-label="View">
      {segs.map(({ key, label, Ico }) => (
        <S.ViewSeg
          key={key}
          active={mode === key ? 1 : 0}
          aria-pressed={mode === key}
          aria-label={label}
          title={label}
          onClick={() => onChange && onChange(key)}
        >
          <Ico size={18} />
        </S.ViewSeg>
      ))}
    </S.ViewToggle>
  );
}

function SelectionBar({ count, total, onDelete, onClear, onSelectAll }) {
  const allSelected = total > 0 && count >= total;
  return (
    <S.SelectionBar data-testid="selection-bar">
      <span className="count">{count} selected</span>
      <button
        type="button"
        onClick={() => onSelectAll && onSelectAll(!allSelected)}
        data-testid="selection-bar-select-all"
        aria-pressed={allSelected}
      >
        {allSelected ? "Deselect all" : "Select all"}
      </button>
      <button type="button" disabled aria-disabled="true" title={FOLDERS_COMING_SOON}>Move to Folder (coming soon)</button>
      <button type="button" className="danger" onClick={onDelete}>
        <IconTrash size={15} data-testid="selection-bar-trash-icon" /> Delete
      </button>
      <button type="button" onClick={onClear}>Clear selection</button>
    </S.SelectionBar>
  );
}

function Toolbar({ query, onQuery, sortLabel, onSort, mode, onMode, onUpload, uploading }) {
  return (
    <S.RightControls>
      {/* ML-UP-02: "Upload media" lived ONLY in the empty state, so once a user
          had a single file there was no way to add another. The real upload entry
          point lives on the toolbar too, present in every populated state.
          Guarded by `onUpload &&` so a consumer that wires no handler gets no
          button (the reuse picker never wants one). */}
      {onUpload && (
        <S.UploadButton
          type="button"
          onClick={onUpload}
          disabled={uploading}
          aria-busy={uploading ? "true" : undefined}
        >
          <IconUpload size={16} /> {uploading ? "Uploading…" : "Upload"}
        </S.UploadButton>
      )}
      <S.SearchBox>
        <IconSearch size={18} />
        <input
          value={query}
          onChange={(e) => onQuery && onQuery(e.target.value)}
          placeholder="Search media"
          aria-label="Search media"
        />
      </S.SearchBox>
      <S.SortButton
        onClick={onSort}
        aria-label={`Sort order: ${sortLabel}. Activate to change the sort order.`}
        title={`Sort: ${sortLabel} — click to change`}
      >
        Sort: {sortLabel} <IconSort size={16} />
      </S.SortButton>
      <ViewToggle mode={mode} onChange={onMode} />
    </S.RightControls>
  );
}

function Footer({ total, perPage, page, onPerPage, onPage }) {
  const pages = pageCount(total, perPage);
  return (
    <S.Footer>
      <span className="count">{total} items</span>
      <S.Pager>
        <S.PerPage>
          <span className="label">Per page</span>
          <span className="opts">
            {PER_PAGE_OPTIONS.map((n) => (
              <S.PageOpt key={n} active={perPage === n ? 1 : 0} onClick={() => onPerPage && onPerPage(n)} aria-pressed={perPage === n}>
                {n}
              </S.PageOpt>
            ))}
          </span>
        </S.PerPage>
        <S.PagerNav>
          <button type="button" aria-label="Previous page" disabled={page <= 1} onClick={() => onPage && onPage(page - 1)}>
            <IconChevronLeft size={18} />
          </button>
          <span className="page">Page {page} of {pages}</span>
          <button type="button" aria-label="Next page" disabled={page >= pages} onClick={() => onPage && onPage(page + 1)}>
            <IconChevronRight size={18} />
          </button>
        </S.PagerNav>
      </S.Pager>
    </S.Footer>
  );
}

function EmptyState({ onUpload, uploading }) {
  return (
    <S.EmptyWrap data-testid="media-empty">
      <EmptyArt />
      <div className="h">Everything you upload lives here</div>
      <div className="p">Upload once — reuse it on any Wheel.</div>
      <S.UploadCta
        onClick={onUpload}
        disabled={uploading}
        aria-busy={uploading ? "true" : undefined}
      >
        <IconUpload size={18} /> {uploading ? "Uploading…" : "Upload media"}
      </S.UploadCta>
    </S.EmptyWrap>
  );
}

// Line-art stacked media cards + sparkles (deck empty art). No emoji.
function EmptyArt() {
  return (
    <svg width="240" height="150" viewBox="0 0 240 150" fill="none" aria-hidden="true">
      <g transform="rotate(-9 84 78)">
        <rect x="52" y="46" width="64" height="72" rx="8" fill="#D6FEF0" stroke="#29C18C" strokeWidth="2.5" />
        <path d="M74 82l-10 12" stroke="#29C18C" strokeWidth="2.5" strokeLinecap="round" />
        <path d="M78 76l6 3.6-6 3.6z" fill="#29C18C" />
      </g>
      <g transform="rotate(8 168 78)">
        <rect x="132" y="46" width="64" height="72" rx="8" fill="#FFF4E3" stroke="#F79A00" strokeWidth="2.5" />
        <path d="M146 70h36M146 82h28M146 94h32" stroke="#F79A00" strokeWidth="2.5" strokeLinecap="round" />
      </g>
      <g>
        <rect x="92" y="40" width="60" height="80" rx="8" fill="#EFE9FE" stroke="#9B5AF8" strokeWidth="2.5" />
        <circle cx="112" cy="66" r="7" fill="none" stroke="#9B5AF8" strokeWidth="2.5" />
        <path d="M98 110l16-18 12 12 8-8 12 12" fill="none" stroke="#9B5AF8" strokeWidth="2.5" strokeLinejoin="round" />
      </g>
      <path d="M40 40l3 6 6 3-6 3-3 6-3-6-6-3 6-3z" fill="#F59E0B" />
      <path d="M206 30l2.4 5 5 2.4-5 2.4-2.4 5-2.4-5-5-2.4 5-2.4z" fill="#9B5AF8" />
      <path d="M200 108l2 4 4 2-4 2-2 4-2-4-4-2 4-2z" fill="#F472B6" />
    </svg>
  );
}

function LoadingGrid({ cols = 5 }) {
  return (
    <S.Grid cols={cols} data-testid="media-loading">
      {Array.from({ length: 10 }).map((_, i) => (
        <div key={i}>
          <S.Skeleton style={{ width: "100%", aspectRatio: "16 / 10" }} />
          <div style={{ padding: "10px 4px" }}>
            <S.Skeleton style={{ height: 14, width: "70%", marginBottom: 6 }} />
            <S.Skeleton style={{ height: 12, width: "40%" }} />
          </div>
        </div>
      ))}
    </S.Grid>
  );
}

function ErrorState({ onRetry }) {
  return (
    <S.ErrorWrap data-testid="media-error" role="alert">
      {/* Shared ErrorWheel — this state was text-only, so a failure and an empty
          library were visually indistinguishable at a glance. Decorative: the
          wrapper is already role="alert". */}
      <ErrorWheel variant="section" decorative style={{ marginBottom: 16 }} />
      <div className="h">We couldn't load your media</div>
      <div className="p">Something went wrong reaching your library. Please try again.</div>
      <S.RetryButton onClick={onRetry}>Retry</S.RetryButton>
    </S.ErrorWrap>
  );
}

export default function MediaLibraryView({
  status = "ready", // 'loading' | 'error' | 'empty' | 'ready'
  uploading = false,
  storage = { usedLabel: "0 MB", totalLabel: "5 GB", percent: 0 },
  folders = [],
  items = [],
  total = 0,
  activeType = "all",
  query = "",
  sortLabel = "Newest",
  sortKey = "newest",
  viewMode = "large",
  page = 1,
  perPage = 25,
  selectedIds = [],
  onType, onQuery, onSort, onColumnSort, onViewMode, onPage, onPerPage,
  onOpenFolder, onUpload, onPlayItem, onExpandItem, onToggleSelectItem, onRetry, onClearFilters,
  onBulkDelete, onClearSelection, onRenameItem, onSelectAll, onUpgrade,
}) {
  const selecting = selectedIds.length > 0;
  // EW-1290: the onboarding empty state ("Everything you upload lives here") must
  // render ONLY when the LIBRARY is genuinely empty — i.e. status==="empty", which
  // the container derives from the UNFILTERED item count. The old guard also fired
  // on `status==="ready" && total===0 && folders.length===0`, but `folders` is never
  // wired (always []), so ANY search/type filter that narrowed the results to zero
  // while the user DID have media was mistaken for an empty library — hiding the
  // real "No media matches your filters" branch below and telling a user with a full
  // library to "upload once". Filtered-to-zero now correctly falls through to
  // no-results. (fixes C14.4b / C13.8: garbage search rendered the onboarding card.)
  const isEmpty = status === "empty";
  const hasActiveFilter = Boolean(query) || activeType !== "all";

  return (
    <S.Page data-testid="media-library">
      <S.TitleRow>
        <S.Title>Media Library</S.Title>
        <StorageStrip usedLabel={storage.usedLabel} totalLabel={storage.totalLabel} percent={storage.percent} onUpgrade={onUpgrade} exceeded={storage.exceeded} />
      </S.TitleRow>

      <S.Card>
        {isEmpty ? (
          <>
            <S.SectionLabel>Folders <span>(0)</span></S.SectionLabel>
            <S.FoldersRow>
              <S.NewFolderTile type="button" disabled aria-disabled="true" title={FOLDERS_COMING_SOON}>
                <IconPlus size={20} />
                <span>New Folder <span style={{ fontWeight: 400 }}>(coming soon)</span></span>
              </S.NewFolderTile>
            </S.FoldersRow>
            <S.ControlsRow>
              <TypeTabs active={activeType} onChange={onType} />
              <Toolbar query={query} onQuery={onQuery} sortLabel={sortLabel} onSort={onSort} mode={viewMode} onMode={onViewMode} onUpload={onUpload} uploading={uploading} />
            </S.ControlsRow>
            <EmptyState onUpload={onUpload} uploading={uploading} />
          </>
        ) : (
          <>
            <FoldersRow folders={folders} onOpenFolder={onOpenFolder} />
            <S.ControlsRow>
              {selecting ? (
                <SelectionBar
                  count={selectedIds.length}
                  total={items.length}
                  onDelete={onBulkDelete}
                  onClear={onClearSelection}
                  onSelectAll={onSelectAll}
                />
              ) : (
                <TypeTabs active={activeType} onChange={onType} />
              )}
              {/* view toggle omitted while empty; here in list mode we still show it */}
              <Toolbar query={query} onQuery={onQuery} sortLabel={sortLabel} onSort={onSort} mode={viewMode} onMode={onViewMode} onUpload={onUpload} uploading={uploading} />
            </S.ControlsRow>

            {status === "loading" && <LoadingGrid />}
            {status === "error" && <ErrorState onRetry={onUpload && onRetry} />}
            {status === "ready" && total === 0 && (
              <S.ErrorWrap data-testid="media-noresults">
                <div className="h">No media matches your filters</div>
                <div className="p">Try a different type or clear your search.</div>
                {hasActiveFilter && onClearFilters && (
                  <S.RetryButton type="button" onClick={onClearFilters}>
                    Clear filters
                  </S.RetryButton>
                )}
              </S.ErrorWrap>
            )}
            {status === "ready" && total > 0 && (
              viewMode === "list" ? (
                <MediaListTable
                  items={items}
                  selectedIds={selectedIds}
                  onActivate={onToggleSelectItem}
                  onRename={onRenameItem}
                  showKebab={false}
                  sortKey={sortKey}
                  onSort={onColumnSort}
                />
              ) : (
                <MediaGrid
                  items={items}
                  cols={viewMode === "small" ? 8 : 5}
                  small={viewMode === "small"}
                  selectedIds={selectedIds}
                  onPlay={onPlayItem}
                  onExpand={onExpandItem}
                  onToggleSelect={onToggleSelectItem}
                  onRename={onRenameItem}
                  showHoverPill={false}
                />
              )
            )}

            {status === "ready" && total > 0 && (
              <Footer total={total} perPage={perPage} page={page} onPerPage={onPerPage} onPage={onPage} />
            )}
          </>
        )}
      </S.Card>
    </S.Page>
  );
}
